import React from "react";
import {
  IHotelListSliderProps,
  TBrandLogo,
  THotelData,
} from "@/components/organisms/hotel-list-slider";
import { TypeSearch } from "@/components/organisms/hotel-result";
import {
  GroupOption,
  SelectOption as OriginalSelectOption,
} from "@/components/organisms/search-avaliable/SearchAvailableRoom";
import {
  CRM_MIDDLE_LIST_DATASOURCE_ITEM_NAME,
  DEFAULT_ADULTS,
  DEFAULT_AGE_VALUE_PLACEHOLDER,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
} from "@/constants";
import { TRIPLA_BOOKING_URL } from "@/constants/enviroment";
import {
  AmenityServicesQuery,
  HotelAndCityDatasourcesQuery,
  HotelBrandsQuery,
  HotelListQuery,
  JapanArea,
  MessageDatasource,
  PopularImagesQuery,
  ComponentJapanWeather,
  RegionListQuery,
  JapanHotel,
  NewsYearMonthDayItem,
  UploadFile,
  SetOfTag,
} from "@/types/generated";
import { ERoomType, ERoomUnit } from "@/types/roomsList";
import { HotelBrandItemNullable } from "@app/[locale]/brands/[brand_slug]/page";
import fuzzysort from "fuzzysort";
import {
  filterSelectionData,
  generateLinkFromGeneralLink,
  generateTargetFromLink,
  LinkConfig,
} from "@/lib/utils";
import { IBanner } from "@/components/organisms/hotel-result/banner/BannerBrand";
import { orderSelectionList } from "@app/[locale]/mice/utils";
import { ISelectionResponse } from "@/types/mice";
import { IFieldSelectOrder } from "@/types/common";
import { GOOGLE_MAP } from "@/configs/config";
import { TextSectionWithButtonsProps } from "@/components/organisms/Reusables/Sections/TextSectionWithButtons";

export interface SelectOption extends OriginalSelectOption {
  type?: string;
  city?: string;
  lat?: string;
  lng?: string;
  searchNames?: string[];
  hotelItemName?: string;
  bookingId?: string;
  hotelCode?: string;
  citySearchNames?: string[];
  slug?: string;
  zoom?: number;
  useTripla?: boolean;
  externalUrl?: string;
  initial_adult_count?: string;
}

export interface HotelListSelected {
  order: number;
  documentId: string;
}

// Validates a URL format
export const invalidLink = (url: string): boolean => {
  if (!url) return false;

  try {
    const urlObject = new URL(url);

    if (!["http:", "https:"].includes(urlObject.protocol)) {
      return false;
    }

    const hostname = urlObject.hostname;
    if (
      !hostname.includes(".") ||
      hostname.startsWith(".") ||
      hostname.endsWith(".")
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

// Truncates a string to a maximum length
export const truncateByLength = (
  input: string,
  lengthLimit: number,
): string => {
  if (!input) {
    return "";
  }
  return input.slice(0, lengthLimit) + "...";
};

export const HOT_SPRING_AMENITY = "d10d7186e82cc127d45f8355";

// Returns current date in Japan timezone (YYYY-MM-DD)
export const getJapanDate = (): string => {
  const japanDate = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  return japanDate; // Format: YYYY-MM-DD
};

// Formats a number with commas
export const formatNumberPrice = (number: number): string => {
  if (!number) return "";
  return new Intl.NumberFormat("en-US").format(number);
};

// Converts room unit to readable format
export const formatRoomUnit = (unit?: string): string => {
  if (!unit) return "";
  return unit === ERoomUnit.SQUARE_METERS ? "m²" : unit;
};

// Formats a date to YYYY-MM-DD
export const formatDateToYYYYMMDD = (
  dateInput: string | Date | null,
): string => {
  if (!dateInput) return "";
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

type Item = {
  value: number;
  label: string;
};

export function generateListAge(
  max: number,
  ageValuePlaceholder?: string,
): Item[] {
  const label = ageValuePlaceholder || DEFAULT_AGE_VALUE_PLACEHOLDER;
  const list: Item[] = [];
  list.push({ value: -1, label: label }, { value: 0, label: "<1" });

  for (let i = 1; i <= max; i++) {
    list.push({ value: i, label: i.toString() });
  }

  return list;
}

// Adds query params to a URL
export const addParamsToUrl = (
  url: string,
  params: Record<string, string | number | boolean>,
): string => {
  const updatedUrl = new URL(url);

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      updatedUrl.searchParams.set(key, String(value));
    }
  }

  return updatedUrl.toString();
};

// Redirects to Tripla booking page with params
export const redirectBookingCard = (
  params: {
    code: string;
    one_time_access_token?: string;
    checkin: string;
    checkout: string;
    adults: string;
    locale?: string;
    children?: number;
    room_count?: number;
    coupon_code?: string;
    type?: string;
    is_day_use?: boolean;
    order?: string;
    room_type_codes?: string;
    is_including_occupied?: boolean;
    // eslint-disable-next-line @typescript-eslint/array-type
    kids_ages?: Array<{ bed: boolean; age: string; count: number }>;
  },
  isMobile: boolean = true,
) => {
  const { kids_ages, ...restParams } = params;

  const allParams: Record<string, string | number | boolean> = {
    ...restParams,
    ...(kids_ages && { kids_ages: JSON.stringify(kids_ages) }),
  };

  const redirectUrl = addParamsToUrl(
    `${TRIPLA_BOOKING_URL}/booking/result`,
    allParams,
  );

  if (isMobile) {
    globalThis.location.href = redirectUrl;
  } else {
    globalThis.open(redirectUrl, "_blank");
  }
};

// Generates Tripla booking URL for given params
function buildKidsAges(
  rooms:
    | {
        adults: number;
        children?: (
          | {
              age?: { label: string; value: number };
              isBed?: boolean;
            }
          | undefined
        )[];
      }[]
    | undefined,
) {
  type KidAge = {
    bed: boolean;
    age: string;
    count: number;
  };

  const kids_ages: KidAge[] = [];
  if (!rooms || rooms.length === 0) return kids_ages;

  const firstRoom = rooms[0];
  if (!firstRoom?.children) return kids_ages;

  // Normalize children (remove undefined)
  const validChildren = firstRoom.children.filter(Boolean);

  for (const child of validChildren) {
    if (child!.age) {
      kids_ages.push({
        bed: child!.isBed || false,
        age: String(child!.age.label ?? child!.age.value),
        count: 1,
      });
    }
  }

  return kids_ages;
}

export const createTriplaUrl = (params: {
  tripla_code: string;
  checkIn?: Date | null;
  checkout?: Date | null;
  adults?: number | string;
  rooms?: (
    | {
        adults: number;
        children?: (
          | {
              age?: {
                label: string;
                value: number;
              };
              isBed?: boolean;
            }
          | undefined
        )[];
      }
    | undefined
  )[];
  locale?: string;
  type?: string;
  room_type_codes?: string;
  coupon_code?: string;
  one_time_access_token?: string;
}): string => {
  const {
    tripla_code,
    checkIn = DEFAULT_CHECKIN_DATE,
    checkout = DEFAULT_CHECKOUT_DATE,
    adults = DEFAULT_ADULTS,
    rooms = [{ adults: Number(DEFAULT_ADULTS), children: [] }],
    locale,
    type = ERoomType.ROOMS,
    room_type_codes,
    coupon_code,
    one_time_access_token,
  } = params;

  // Clean undefined rooms
  const cleanedRooms = rooms?.filter(Boolean) as {
    adults: number;
    children?: (
      | {
          age?: { label: string; value: number };
          isBed?: boolean;
        }
      | undefined
    )[];
  }[];

  const roomAdults = cleanedRooms?.[0]?.adults ?? adults;
  const roomChildren = cleanedRooms?.[0]?.children?.filter(Boolean).length ?? 0;
  const roomCount = cleanedRooms?.length ?? 1;

  const kids_ages = buildKidsAges(cleanedRooms);

  let url = `${TRIPLA_BOOKING_URL}/#/booking/result?code=${tripla_code}`;
  url += `&checkin=${formatDateToYYYYMMDD(checkIn)}`;
  url += `&checkout=${formatDateToYYYYMMDD(checkout)}`;
  url += `&type=${type}`;
  url += `&adults=${roomAdults}`;
  url += `&children=${roomChildren}`;
  url += `&room_count=${roomCount}`;
  url += `&locale=${locale}`;

  if (room_type_codes) url += `&room_type_codes=${room_type_codes}`;
  if (coupon_code) url += `&coupon_code=${coupon_code}`;
  if (kids_ages && kids_ages.length > 0) {
    url += `&kids_ages=${encodeURIComponent(JSON.stringify(kids_ages))}`;
  }
  if (one_time_access_token) {
    url += `&one_time_access_token=${one_time_access_token}`;
  }

  return url;
};

// Checks if a string contains HTML tags
export const isContainHtml = (str: string) => {
  const pattern = /<\/?[a-z][\s\S]*>/i;
  return pattern.test(str);
};

// Extracts area size from text like "20 m²"
export const extractArea = (text: string) => {
  const regex = /\b\d+(\.\d+)?\s?(m²|square meters?)\b/i;

  const match = regex.exec(text);
  if (match) {
    return match[0]; // full matched string, e.g., "20 m²"
  }

  return null;
};

// Extracts bed sizes from text like "200 cm beds"
export const extractBedSize = (text: string) => {
  const regex = /\b(\d+\s?cm)\s?beds?\b/gi;

  const matches = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    matches.push(match[1]);
  }

  return matches;
};

// Converts string to kebab-case
export const kebabCase = (str: string) => {
  return (
    str
      // Replace camelCase and PascalCase with space between words
      ?.replaceAll(/([a-z])([A-Z])/g, "$1 $2")
      // Replace underscores and non-alphanumeric characters with spaces
      ?.replaceAll(/\W+/g, " ")
      // Trim whitespace, convert to lowercase, and replace spaces with hyphens
      ?.trim()
      ?.toLowerCase()
      ?.replaceAll(/\s+/g, "-")
  );
};

export const TOKYOID = "072c6bc355474cad7efb4156";
export const BOTTOM_NAVIGATION = "nhdllm0n7wqokx823jsmryun";
export const BOTTOM_NAVIGATION_ITEM_NAME = "Bottom Navigation";
export const LOGIN_BUTTON = "e7tqbkrldghwlxi37wuvx5f5";
export const CALL_BUTTON = "av0b5fs0tzpjwa1hdd6d8wph";
export const REVERSE_BUTTON = "aacnmw13zpx2wc55zz8g6pov";
export const LOGIN_BUTTON_ITEM_NAME = "Sign-in";
export const REVERSE_BUTTON_ITEM_NAME = "Reverse";
export const CALL_BUTTON_ITEM_NAME = "Call";
export const RESORT_TAG_ITEM_NAME = "Resort";
export const GO_TO_PASS_ITEM_NAME = "Go To Pass";
export const TOKYO_ITEM_NAME = "Tokyo";
export const HOKKAIDO_ITEM_NAME = "Hokkaido";
export const OSAKA_ITEM_NAME = "Osaka";
export const LIST_CITY_IN_HOTEL_LIST_ALL = [TOKYO_ITEM_NAME, OSAKA_ITEM_NAME];

/**
 * Helper function to determine if an area should be filtered out
 */
const shouldFilterArea = (
  area: JapanArea,
  hotelSliderSections: IHotelListSliderProps[] | undefined,
  currenCity: string | undefined,
  currentBrand: HotelBrandItemNullable | undefined,
  cityList: string[] | undefined,
): boolean => {
  if (!area) return true;

  if (hotelSliderSections) {
    if (currenCity && area.documentId !== currenCity) return true;
    if (
      !currenCity &&
      Array.isArray(cityList) &&
      !cityList.includes(area.documentId)
    ) {
      return true;
    }
  } else {
    if (currenCity && !currentBrand && area.documentId !== currenCity)
      return true;
    if (
      currentBrand &&
      !currenCity &&
      Array.isArray(cityList) &&
      !cityList.includes(area.documentId)
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Helper function to create hotel options from filtered hotels
 */
const createHotelOptions = (
  filteredHotels: Maybe<JapanHotel>[] | undefined,
  area: JapanArea,
  hotelSearchNamesMap: Map<string, Set<string>>,
): SelectOption[] => {
  const options: SelectOption[] = [];

  if (!filteredHotels) return options;

  for (const hotel of filteredHotels) {
    if (hotel?.documentId) {
      const searchNames = hotelSearchNamesMap.get(hotel.documentId);
      const hotelOption: SelectOption = {
        label: hotel?.properties?.hotel_name || hotel?.item_name || "",
        hotelItemName: hotel?.item_name || "",
        value: hotel?.documentId ?? "",
        bookingId: hotel?.external_ids?.tripla_booking_code ?? "",
        hotelCode: hotel?.external_ids?.tripla_backend_code ?? "",
        useTripla: hotel?.external_ids?.use_tripla ?? false,
        externalUrl: hotel?.redirect?.external_url ?? "",
        type: TypeSearch.HOTEL,
        city: area?.item_name ?? "",
        lat: hotel.properties?.latitude ?? "",
        lng: hotel.properties?.longitude ?? "",
        searchNames: searchNames
          ? Array.from(searchNames)
          : [hotel?.properties?.hotel_name || hotel?.item_name || ""].filter(
              Boolean,
            ),
        citySearchNames: [area?.item_name, area?.area_name].filter(
          Boolean,
        ) as string[],
        slug: hotel?.slug || "",
        initial_adult_count:
          hotel?.force_default_no_of_guest?.default_no_of_guests ?? undefined,
      };
      options.push(hotelOption);
    }
  }

  return options;
};

// Groups Japan areas and their hotels for select/dropdown usage
export const groupJapanAreas = (
  japanAreas: JapanArea[],
  allLocaleHotels: Record<string, JapanHotel[]> | null | undefined,
  currenCity?: string,
  cityLabel?: string,
  hotelLabel?: string,
  currentBrand?: HotelBrandItemNullable,
  hotelSliderSections?: IHotelListSliderProps[],
): GroupOption[] => {
  const hotelSearchNamesMap = new Map<string, Set<string>>();

  // Build map of hotel documentId -> set of search names
  if (allLocaleHotels) {
    const hotelsArray = Object.values(allLocaleHotels).filter(Boolean).flat(); // flatten array of arrays

    for (const hotel of hotelsArray) {
      if (!hotel?.documentId) continue;

      const namesSet =
        hotelSearchNamesMap.get(hotel.documentId) || new Set<string>();

      const names = [
        hotel.properties?.hotel_name,
        hotel.item_name,
        hotel.display_name,
      ];
      for (const name of names) {
        if (name) namesSet.add(name);
      }

      hotelSearchNamesMap.set(hotel.documentId, namesSet);
    }
  }

  const hotelAreaNames = japanAreas.map((i) => ({
    hotels: i?.hotels.map((h) => ({ documentId: h?.documentId })),
    documentId: i?.documentId,
  }));
  let cityList: string[] | undefined = undefined;
  let CurrentHotelListCheckById: (string | undefined)[] | undefined = undefined;

  // Collect hotel IDs from slider sections if available
  if (hotelSliderSections) {
    const seenCitySet = new Set<string>();
    CurrentHotelListCheckById = hotelSliderSections.flatMap((item) =>
      item.data.map((hotel) => hotel.documentId),
    );

    cityList = CurrentHotelListCheckById?.map((id) =>
      getHotelAreaDocumentId(hotelAreaNames, id),
    ).filter((docId): docId is string => {
      if (!docId || seenCitySet.has(docId)) return false;
      seenCitySet.add(docId);
      return true;
    });
  }

  // Filter and group areas and hotels
  const dataFilter = (japanAreas || [])
    ?.filter(
      (area) =>
        !shouldFilterArea(
          area,
          hotelSliderSections,
          currenCity,
          currentBrand,
          cityList,
        ),
    )
    .reduce((groups: GroupOption[], area) => {
      const cityOption: SelectOption = {
        label: area?.area_name ?? "",
        value: area?.documentId ?? "",
        type: TypeSearch.CITY,
        lat: area.latitude ?? "",
        lng: area.longitude ?? "",
        searchNames: [area?.area_name, area?.item_name].filter(
          Boolean,
        ) as string[],
        slug: area?.slug_attraction || "",
        zoom: area.map_zoom_ratio ? Number(area.map_zoom_ratio) : 11,
      };
      let cityGroup = groups.find(
        (g) => g.label === cityLabel || g.label === "City",
      );
      if (!cityGroup) {
        cityGroup = {
          label: cityLabel || "City",
          options: [] as SelectOption[],
        };
        groups.push(cityGroup);
      }
      cityGroup.options.push(cityOption);

      const hotelGroup: GroupOption = {
        label: hotelLabel || "Hotel",
        options: [] as SelectOption[],
      };
      const cloneHotels = structuredClone(area?.hotels || []) as JapanHotel[];

      const selectedHotelsCMS = area?.selected_hotels || [];
      const selectedHotels = currentBrand
        ? area?.hotels
        : filterSelectionData(cloneHotels, selectedHotelsCMS);
      const filteredHotels = selectedHotels?.filter((hotel) => {
        if (hotelSliderSections) {
          return CurrentHotelListCheckById?.includes(hotel?.documentId);
        }
        return true;
      });

      const hotelOptions = createHotelOptions(
        filteredHotels,
        area,
        hotelSearchNamesMap,
      );
      hotelGroup.options = hotelOptions;

      if (hotelGroup?.options?.length > 0) {
        const existingHotelGroup = groups.find(
          (g) => g.label === hotelLabel || g.label === "Hotel",
        );
        if (existingHotelGroup) {
          existingHotelGroup.options = [
            ...existingHotelGroup.options,
            ...hotelGroup.options,
          ];
        } else {
          groups.push(hotelGroup);
        }
      }
      return groups;
    }, [] as GroupOption[]);
  return dataFilter;
};

// Returns list of banners for a given brand
export const getBrandBannerList = (brand: HotelBrandsQuery): IBanner[] => {
  if (!brand?.homeBrand) return [];

  const topBrands = brand.homeBrand.top_list_datasources.flatMap(
    (ds) => ds?.brands || [],
  );
  const bottomBrands = brand.homeBrand.bottom_list_datasources.flatMap(
    (ds) => ds?.brands || [],
  );
  const middleBrands = brand.homeBrand.middle_list_datasources.find(
    (ds) => ds?.item_name === CRM_MIDDLE_LIST_DATASOURCE_ITEM_NAME,
  );
  const allBrands = [...topBrands, ...bottomBrands];
  const brands = allBrands
    .map((b) => {
      if (b?.name) {
        return {
          itemName: b.name,
          brandName: b?.title || b.name,
          brandDesktopUrl: b.brand_desktop_image?.[0]?.cloudflare_url || "",
          brandMobileUrl: b.brand_mobile_image?.[0]?.cloudflare_url || "",
          brandLogoUrl: b.brand_logo?.cloudflare_url || "",
          alt: b?.brand_logo?.alternativeText || "",
          caption: b?.brand_logo?.caption || "",
          description: b.description || "",
          isShowFilter: b.show_filter_in_brand_top,
        };
      }
      return null;
    })
    .filter(Boolean) as IBanner[];
  const middleData = middleBrands?.item_name
    ? {
        itemName: middleBrands?.item_name,
        brandName: middleBrands.title ?? "",
        brandDesktopUrl: middleBrands.image_one_desktop?.cloudflare_url || "",
        brandMobileUrl: middleBrands.image_one_mobile?.cloudflare_url || "",
        brandLogoUrl: middleBrands.brand_logo?.cloudflare_url || "",
        description: middleBrands.description || "",
        isShowFilter: middleBrands.show_filter_in_brand_top,
      }
    : null;
  if (middleData) {
    brands.push(middleData);
  }
  return brands;
};

interface GroupDataHotelListParams {
  hotel: HotelListQuery;
  hotelAndCity: HotelAndCityDatasourcesQuery;
  brand: HotelBrandsQuery;
  amenity: AmenityServicesQuery;
  cityId?: string;
  locale: string;
  regionData?: RegionListQuery;
  hotelThumbnailImages?: Map<string | undefined, Maybe<UploadFile> | undefined>;
}

// Deduplicates hotel list and optionally orders by hotelOrderData
const deduplicateHotels = (
  hotels: THotelData[],
  hotelOrderData?: HotelListSelected[] | null,
): THotelData[] => {
  const resolveDuplicateData = Array.from(
    new Map(hotels.map((h) => [h.documentId, h])).values(),
  );
  if (!hotelOrderData) {
    return resolveDuplicateData;
  }
  const orderMap = new Map(hotelOrderData.map((h) => [h.documentId, h.order]));
  const hotelsWithOrder: { hotel: THotelData; order: number }[] = [];
  for (const hotel of resolveDuplicateData) {
    const order =
      orderMap.get(hotel.documentId || "") || Number.MAX_SAFE_INTEGER;
    hotelsWithOrder.push({ hotel, order });
  }
  hotelsWithOrder.sort((a, b) => a.order - b.order);
  return hotelsWithOrder.map((h) => h.hotel);
};
export enum HotelByTravelTagPage {
  Resort = "Resort",
  PetList = "Pets List",
  Onsen = "Onsen",
}
interface GroupDataBrandListParams extends GroupDataHotelListParams {
  currentBrand?: HotelBrandItemNullable;
  // Matched against a travel-goal tag's `item_name`. Used by the static hotels-*
  // pages, which hardcode a HotelByTravelTagPage value.
  tag?: HotelByTravelTagPage | string;
  // Travel-goal documentIds to filter by, taking precedence over `tag`. CMS-driven
  // tag pages pass these straight from the Travel Goals Tags picker, so no name
  // lookup is needed. A hotel matches if it carries any of them.
  tagDocumentIds?: string[];
  recommendedHotel?: IFieldSelectOrder[];
}
export enum TYPE_GROUP {
  BASIC = "basic",
  NEAR_BY_STATION = "nearByStation",
  WITH_HOT_SPRINGS = "withHotSprings",
}

// Gets area name for a hotel
export const getHotelAreaName = (
  areas: {
    hotels?: { documentId?: string | null }[];
    area_name?: string | null;
  }[],
  hotelId?: string,
) => {
  return (
    areas.find((i) => i?.hotels?.find((h) => h?.documentId === hotelId))
      ?.area_name || ""
  );
};

// Gets area documentId for a hotel
const getHotelAreaDocumentId = (
  areas: {
    hotels?: { documentId?: string | null }[];
    documentId?: string | null;
  }[],
  hotelId?: string,
) => {
  return (
    areas.find((i) => i?.hotels?.find((h) => h?.documentId === hotelId))
      ?.documentId || ""
  );
};

// Groups hotel data for hotel list page, organizes by city, region, hot spring, and slider sections
export const groupDataHotelList = ({
  hotel,
  hotelAndCity,
  brand,
  amenity,
  cityId,
  regionData,
  hotelThumbnailImages,
}: GroupDataHotelListParams) => {
  // Merge top and bottom brand data for homepage
  const mergedBrands: TBrandLogo[] = brand?.homeBrand
    ? [
        ...(brand.homeBrand.top_list_datasources?.flatMap(
          (ds) => ds?.brands || [],
        ) || []),
        ...(brand.homeBrand.bottom_list_datasources?.flatMap(
          (ds) => ds?.brands || [],
        ) || []),
      ].map((b) => {
        return {
          documentId: b?.documentId || "",
          name: b?.name || "",
          cloudflare_url: b?.brand_logo?.cloudflare_url || "",
          url: b?.brand_logo?.url || "",
        };
      })
    : [];

  // Filter areas by city if cityId is provided
  const filteredAreas = cityId
    ? hotelAndCity.japanAreas.filter((item) => item?.documentId === cityId)
    : hotelAndCity.japanAreas;

  // Map areas to hotel document IDs for lookup
  const hotelAreaNames = filteredAreas.map((i) => ({
    hotels: i?.hotels.map((h) => ({ documentId: h?.documentId })),
    area_name: i?.area_name,
  }));

  // Map raw hotel data to structured hotel objects
  const hotelData: THotelData[] = hotel
    ? hotel.japanHotels.map((item) => {
        const CurrentDataBrand = brand.hotelBrands.find(
          (data) =>
            data?.documentId === item?.properties?.brand?.[0]?.documentId,
        );
        const thumbnail = hotelThumbnailImages?.get(item?.documentId);
        return {
          documentId: item?.documentId,
          name: item?.properties?.hotel_name || item?.name,
          reviewCount: item?.properties?.reviews,
          rating: item?.properties?.review_score,
          weather: item?.weather as ComponentJapanWeather | undefined,
          brandDocId: item?.properties?.brand?.[0]?.documentId,
          city:
            item?.properties?.prefecture && item.properties.prefecture !== ""
              ? item.properties.prefecture
              : getHotelAreaName(hotelAreaNames, item?.documentId),
          description: item?.properties?.hotel_overview_description,
          image: thumbnail || null,
          brand:
            item?.properties?.brand &&
            item?.properties?.brand?.length > 0 &&
            item?.properties?.brand[0]?.documentId
              ? item?.properties?.brand[0]?.documentId
              : "",
          service: item?.amenities_and_services?.amenities_and_services_customs,
          btnText: hotel.textButton[0]?.result?.view_hotel ?? "",
          benefits: item?.amenities_and_services?.hotel_benefits?.map(
            (item: { documentId: string }) => item.documentId,
          ),
          lat: item?.properties?.latitude,
          lng: item?.properties?.longitude,
          nearByStation: item?.meetings_space?.nearby_stations?.length,
          slug: item?.slug,
          withHotSprings: item?.amenities_and_services
            ?.amenities_and_services_customs
            ? item?.amenities_and_services?.amenities_and_services_customs.some(
                (data: { documentId: string }) =>
                  data.documentId === HOT_SPRING_AMENITY,
              )
            : false,
          externalUrl: item?.redirect?.external_url,
          triplaCode: item?.external_ids?.tripla_backend_code,
          brandItemName: CurrentDataBrand?.item_name,
          triplaBookingCode: item?.external_ids?.tripla_booking_code,
        };
      })
    : [];
  const hotelSliderSections: IHotelListSliderProps[] = [];

  // Create slider sections grouped by area
  for (const areaItem of filteredAreas) {
    const hotelIds = new Set(
      areaItem?.hotels?.map((hotel) => hotel?.documentId).filter(Boolean),
    );

    const matchedHotels = hotelData.filter((item) =>
      hotelIds.has(item.documentId),
    );

    if (!matchedHotels?.length) continue;

    const baseSection = {
      brandLogoData: mergedBrands,
      hotelBrandData: brand.hotelBrands,
      amenitiesAndServiceData: amenity,
      location: areaItem?.item_name,
      locationId: areaItem?.documentId,
      locationItemName: areaItem?.area_name,
      selectedHotels: areaItem?.selected_hotels,
    };

    const listHotelOnCurrentSection = deduplicateHotels(
      matchedHotels,
      areaItem?.selected_hotels || ([] as HotelListSelected[]),
    );

    hotelSliderSections.push({
      ...baseSection,
      data: listHotelOnCurrentSection,
      typeGroup: TYPE_GROUP.BASIC,
    });
  }

  //handle three list city in hotel list all
  const cityListInHotelList: IHotelListSliderProps[] =
    LIST_CITY_IN_HOTEL_LIST_ALL.map((item) => {
      const findCityData = hotelSliderSections.find(
        (data) => data.location && data.location === item,
      );
      return findCityData ?? null;
    }).filter(
      (item): item is (typeof hotelSliderSections)[number] => item !== null,
    );
  const hotelWithHotSpring = hotelData.filter((item) => item.withHotSprings);

  const hotSpringSection: IHotelListSliderProps = {
    data: deduplicateHotels(hotelWithHotSpring),
    brandLogoData: mergedBrands,
    hotelBrandData: brand?.hotelBrands,
    amenitiesAndServiceData: amenity,
    location: "",
    typeGroup: TYPE_GROUP.BASIC,
  };

  // Arrange hotels by region
  const selectedRegions =
    regionData?.datasourceJapans[0]?.region_datasource?.selected_regions ?? [];

  const sortedSelectedRegions = [...selectedRegions].sort(
    (a, b) => a.order - b.order,
  );
  const arrangerDataRegion: IHotelListSliderProps[] = (
    sortedSelectedRegions ?? []
  ).flatMap(({ documentId }: { documentId: string }) => {
    const currentRegion =
      regionData?.datasourceJapans[0]?.region_datasource?.regions.find(
        (region) => region?.documentId === documentId,
      );
    if (!currentRegion || !Array.isArray(currentRegion.cities)) return [];
    const sectionMap = new Map(
      hotelSliderSections
        .filter(
          (section) =>
            section?.locationId && section.typeGroup === TYPE_GROUP.BASIC,
        )
        .map((section) => [section.locationId, section]),
    );
    const hotelSections = [...currentRegion.cities]
      .sort((a, b) => a.order - b.order)
      .map((city) => sectionMap.get(city.documentId))
      .filter(Boolean);
    if (!hotelSections?.length) return [];
    const hotelList = hotelSections.flatMap((s) => s?.data ?? []);
    const basicHotelList: IHotelListSliderProps = {
      data: hotelList,
      brandLogoData: mergedBrands,
      hotelBrandData: brand.hotelBrands,
      amenitiesAndServiceData: amenity,
      location: currentRegion?.display_name ?? "",
      typeGroup: TYPE_GROUP.BASIC,
      locationId: currentRegion.documentId,
    };
    const result: IHotelListSliderProps[] = [basicHotelList];
    return result;
  });
  return {
    cityListInHotelList,
    arrangerDataRegion,
    hotelSliderSections,
    hotSpringSection,
  };
};

// Group hotels by brand for display in hotel list
export const groupDataBrandList = async ({
  hotel,
  hotelAndCity,
  brand,
  amenity,
  currentBrand,
  hotelThumbnailImages,
}: GroupDataBrandListParams) => {
  // Merge brand logos from homeBrand top & bottom lists
  const mergedBrands: TBrandLogo[] = brand.homeBrand
    ? [
        ...brand.homeBrand.top_list_datasources.flatMap((ds) => ds?.brands),
        ...brand.homeBrand.bottom_list_datasources.flatMap((ds) => ds?.brands),
      ].map((b) => ({
        documentId: b?.documentId,
        name: b?.name,
        cloudflare_url: b?.brand_logo?.cloudflare_url,
        url: b?.brand_logo?.url,
      }))
    : [];
  if (!currentBrand) {
    return [];
  }

  // Map hotel areas for fallback city names
  const hotelAreaNames = hotelAndCity.japanAreas.map((i) => ({
    hotels: i?.hotels.map((h) => ({ documentId: h?.documentId })),
    area_name: i?.area_name,
  }));

  // Filter hotels that belong to current brand top selection
  const orderHotels: ISelectionResponse[] = currentBrand.hotel_in_brand_top;
  const hotelList = hotel?.japanHotels.filter((item) =>
    orderHotels?.some(
      (activityItem) => item?.documentId === activityItem?.documentId,
    ),
  );
  const orderSelectionListHotel = orderSelectionList(hotelList, orderHotels);

  // Map hotel data for UI consumption
  const hotelData: THotelData[] = orderSelectionListHotel
    ? orderSelectionListHotel.map((item) => {
        const thumbnail = hotelThumbnailImages?.get(item?.documentId);
        const brandItemName = brand.hotelBrands.find(
          (data) =>
            data?.documentId === item?.properties?.brand?.[0]?.documentId,
        )?.item_name;

        return {
          documentId: item?.documentId,
          name: item?.properties?.hotel_name || item?.name,
          reviewCount: item?.properties?.reviews,
          weather: item?.weather as ComponentJapanWeather | undefined,
          brandDocId: currentBrand.documentId,
          rating: item?.properties?.review_score,
          city:
            item?.properties?.prefecture && item.properties.prefecture !== ""
              ? item.properties.prefecture
              : getHotelAreaName(hotelAreaNames, item?.documentId),
          description: item?.properties?.hotel_overview_description,
          image: thumbnail || "",
          brand: currentBrand,
          service: item?.amenities_and_services?.amenities_and_services_customs,
          btnText: hotel.textButton[0]?.result?.view_hotel ?? "",
          benefits: item?.amenities_and_services?.hotel_benefits?.map(
            (b: { documentId: string }) => b.documentId,
          ),
          lat: item?.properties?.latitude,
          lng: item?.properties?.longitude,
          nearByStation: item?.meetings_space?.nearby_stations?.length,
          slug: item?.slug,
          withHotSprings: item?.amenities_and_services
            ?.amenities_and_services_customs
            ? item?.amenities_and_services?.amenities_and_services_customs.some(
                (data: { documentId: string }) =>
                  data.documentId === HOT_SPRING_AMENITY,
              )
            : false,
          triplaCode: item?.external_ids?.tripla_backend_code,
          externalUrl: item?.redirect?.external_url,
          brandItemName: brandItemName,
          triplaBookingCode: item?.external_ids?.tripla_booking_code,
        };
      })
    : [];
  if (hotelData?.length === 0) {
    return [];
  }

  // Deduplicate hotels and prepare basic hotel list section
  const basicHotelList: IHotelListSliderProps = {
    data: deduplicateHotels(hotelData),
    brandLogoData: mergedBrands,
    hotelBrandData: brand.hotelBrands,
    amenitiesAndServiceData: amenity,
    location: "",
    typeGroup: TYPE_GROUP.BASIC,
    locationId: "",
  };
  return [basicHotelList];
};

// Group hotels by travel goal tags for filtering UI
export const groupDataByTravelTagList = ({
  hotel,
  hotelAndCity,
  brand,
  amenity,
  currentBrand,
  hotelThumbnailImages,
  tag,
  tagDocumentIds,
  recommendedHotel,
}: GroupDataBrandListParams): {
  hotelSliderSection: IHotelListSliderProps[];
  currentTag?: SetOfTag;
} => {
  const mergedBrands: TBrandLogo[] = brand.homeBrand
    ? [
        ...brand.homeBrand.top_list_datasources.flatMap((ds) => ds?.brands),
        ...brand.homeBrand.bottom_list_datasources.flatMap((ds) => ds?.brands),
      ].map((b) => ({
        name: b?.name,
        cloudflare_url: b?.brand_logo?.cloudflare_url,
        url: b?.brand_logo?.url,
      }))
    : [];

  // Resolve which travel goals to filter by.
  //
  // CMS-driven tag pages pass documentIds straight from the Travel Goals Tags
  // picker, which needs no lookup. The static hotels-* pages pass a hardcoded name
  // (HotelByTravelTagPage), which still has to be resolved against
  // Content - Tags -> travel_goals_tags.
  const selectedTagIds = new Set(tagDocumentIds ?? []);
  const currentTagId = tagDocumentIds?.length
    ? (hotel.tags[0]?.travel_goals_tags.find(
        (item) => item?.documentId && selectedTagIds.has(item.documentId),
      ) ?? ({ documentId: tagDocumentIds[0] } as SetOfTag))
    : hotel.tags[0]?.travel_goals_tags.find(
        (item) => tag && item?.item_name === tag,
      );

  // A hotel qualifies if it carries any of the selected goals.
  const matchesSelectedTag = (documentId?: string) =>
    documentId
      ? selectedTagIds.size
        ? selectedTagIds.has(documentId)
        : documentId === currentTagId?.documentId
      : false;

  // Map hotel areas for fallback city names
  const hotelAreaNames = hotelAndCity.japanAreas.map((i) => ({
    hotels: i?.hotels.map((h) => ({ documentId: h?.documentId })),
    area_name: i?.area_name,
  }));

  // Filter and map hotels that have the selected travel tag
  const hotelData: THotelData[] = hotel
    ? hotel.japanHotels
        .filter((item) =>
          item?.tags?.travel_goals_tags?.some((data: { documentId: string }) =>
            matchesSelectedTag(data?.documentId),
          ),
        )

        .map((item) => {
          const CurrentDataBrand = brand.hotelBrands.find(
            (data) =>
              data?.documentId === item?.properties?.brand?.[0]?.documentId,
          );
          const thumbnail = hotelThumbnailImages?.get(item?.documentId);
          return {
            documentId: item?.documentId,
            name: item?.properties?.hotel_name || item?.name,
            reviewCount: item?.properties?.reviews,
            weather: item?.weather as ComponentJapanWeather | undefined,
            brandDocId: item?.properties?.brand?.[0]?.documentId,
            rating: item?.properties?.review_score,
            city:
              item?.properties?.prefecture && item.properties.prefecture !== ""
                ? item.properties.prefecture
                : getHotelAreaName(hotelAreaNames, item?.documentId),
            description: item?.properties?.hotel_overview_description,
            image: thumbnail || "",
            brand: currentBrand,
            service:
              item?.amenities_and_services?.amenities_and_services_customs,
            btnText: hotel.textButton[0]?.result?.view_hotel ?? "",
            benefits: item?.amenities_and_services?.hotel_benefits?.map(
              (b: { documentId: string }) => b.documentId,
            ),
            lat: item?.properties?.latitude,
            lng: item?.properties?.longitude,
            nearByStation: item?.meetings_space?.nearby_stations?.length,
            slug: item?.slug,
            withHotSprings: item?.amenities_and_services
              ?.amenities_and_services_customs
              ? item?.amenities_and_services?.amenities_and_services_customs.some(
                  (data: { documentId: string }) =>
                    data.documentId === HOT_SPRING_AMENITY,
                )
              : false,
            triplaBookingCode: item?.external_ids?.tripla_booking_code,
            triplaCode: item?.external_ids?.tripla_backend_code,
            externalUrl: item?.redirect?.external_url,
            brandItemName: CurrentDataBrand?.item_name,
            currentTag: currentTagId as SetOfTag,
          };
        })
    : [];
  if (hotelData?.length === 0) {
    return { hotelSliderSection: [] };
  }

  // Deduplicate hotels and create single section for selected tag
  const hotelSliderSections: IHotelListSliderProps[] = [];
  hotelSliderSections.push({
    data: deduplicateHotels(hotelData, recommendedHotel),
    brandLogoData: mergedBrands,
    hotelBrandData: brand.hotelBrands,
    amenitiesAndServiceData: amenity,
    location: "",
    typeGroup: TYPE_GROUP.BASIC,
  });
  return {
    hotelSliderSection: hotelSliderSections,
    currentTag: currentTagId as SetOfTag,
  };
};

// Map popular images to UI-friendly objects with proper links
export const mapDataPopularImages = (
  popular: PopularImagesQuery,
  locale: string = "ja",
) => {
  const selectedFeatureIds: string[] =
    popular?.homeDatasourcesFeaturesBrandTops?.[0]?.list_features.map(
      (img: { documentId: string }) => img.documentId,
    ) || [];
  const selectedFeatures =
    popular?.homeDatasourcesFeaturesBrandTops?.[0]?.list_features || [];
  const listImages = popular?.features?.[0]?.items || [];

  // Filter images that are part of the selected feature IDs
  const filteredFeatureImages = listImages.filter(
    (featureImage) =>
      featureImage?.documentId &&
      selectedFeatureIds.includes(featureImage.documentId),
  );

  // Sort images by order defined in the feature list
  const dataFeatureImagesSort = filteredFeatureImages.toSorted((a, b) => {
    const orderA: number =
      selectedFeatures?.find(
        (img: { order: number; documentId: string }) =>
          img.documentId === a?.documentId,
      )?.order || 0;

    const orderB: number =
      selectedFeatures?.find(
        (img: { order: number; documentId: string }) =>
          img.documentId === b?.documentId,
      )?.order || 0;

    return orderA - orderB;
  });

  // Map to front-end structure with generated links
  const popularImages = dataFeatureImagesSort.map((item) => ({
    id: item?.documentId || "",
    src: item?.thumbnail?.cloudflare_url || item?.thumbnail?.url || "",
    alt: item?.thumbnail?.alternativeText || "",
    caption: item?.thumbnail?.caption || "",
    title: item?.item_name || "",
    name: item?.item_name || "",
    url: generateLinkFromGeneralLink(item?.item_link, locale) || "#",
    urlTarget: generateTargetFromLink(item?.item_link),
  }));
  return popularImages;
};

// Return suffix for day numbers (1st, 2nd, 3rd, 4th...)
export const getDaySuffix = (day: number) => {
  if (day % 10 === 1 && day !== 11) return "st";
  if (day % 10 === 2 && day !== 12) return "nd";
  if (day % 10 === 3 && day !== 13) return "rd";
  return "th";
};

// Retrieve validation message for a given key
export const getValidationMessagse = (
  key: string,
  messages: MessageDatasource[],
) => {
  if (!messages || messages?.length === 0) return "";
  const message = messages.find((msg) => msg?.item_name === key);
  return message?.properties?.value || "";
};

// Map service IDs to service objects including CSS classes for icons
export const getServices = (
  serviceIds: string[],
  amenitiesAndServiceData: AmenityServicesQuery,
) => {
  const iconIds =
    amenitiesAndServiceData?.contentAmenitiesAndServices?.[0]?.custom.filter(
      (i) => serviceIds.includes(i?.documentId as string),
    );

  if (iconIds) {
    // Map CSS classes for icons by documentId
    const cssClasses = amenitiesAndServiceData?.iconGroups?.flatMap(
      (item) => item?.css_classes ?? [],
    );
    // Create a map to lookup of css_class by documentId
    const cssClassMap = cssClasses.reduce<Record<string, string>>(
      (acc, cur) => {
        if (cur?.documentId && cur?.properties?.css_class) {
          acc[cur.documentId] = cur.properties.css_class;
        }
        return acc;
      },
      {},
    );

    const mergedIcons = iconIds.map((item) => ({
      item_name: item?.item_name,
      documentId: item?.documentId,
      side_popup_icon:
        cssClassMap[item?.side_popup_icon?.id] ||
        cssClassMap[item?.side_popup_icon?.[0]?.documentId] ||
        null,
      title: item?.title,
    }));

    return mergedIcons;
  }

  return [];
};

// Generate Google Maps direction link
export const getGoogleMapDirection = (
  address: string,
  placeID: string,
  latitude: string,
  longitude: string,
): string => {
  if (placeID && address) {
    const encodedAddress = encodeURIComponent(address.trim());
    return (
      `${GOOGLE_MAP.BASE_DIRECTION_URL}?api=1&origin=Current+Location` +
      `&destination=${encodedAddress}&destination_place_id=${placeID}`
    );
  }

  return `${GOOGLE_MAP.BASE_MAP_URL}?saddr=Current+Location&daddr=${latitude},${longitude}`;
};

// Split array into chunks of given size
export function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr?.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

type Maybe<T> = T | null | undefined;

// Get preview image for hotel detail page
export const getNewDetailPreviewImage = (
  id: string | null | undefined,
  listHotels: Maybe<JapanHotel>[],
) => {
  if (!id) return undefined;
  const hotel = listHotels.find((hotel) => hotel?.documentId === id);
  const sliderDataId =
    hotel?.datasources[0]?.slider_datasources[0]?.information?.picked_images_desktop?.find(
      (item: { order: number }) => item?.order === 1,
    )?.documentId;
  const imageNews =
    hotel?.hotel_images?.slider_desktop?.find(
      (image) => image?.documentId === sliderDataId,
    )?.image_url || undefined;
  return imageNews;
};

// Get first related hotel ID from news item
export const getNewsRelatedHotelId = (
  item: NewsYearMonthDayItem,
): string | undefined => {
  return item?.tags?.related_hotels?.find(
    (hotel: { order: number; documentId: string }) => hotel?.order === 1,
  )?.documentId;
};

// Remove <style> tags from HTML content
export const cleanStylesFromContent = (content: string): string => {
  return content?.replaceAll(
    /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi,
    "",
  );
};
// Using `any` here is intentional as the function handles dynamic and highly customizable data structures.
export const cityAndHotelFuzzyFilter = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  option: { label: string; data: any },
  inputValue: string,
): boolean => {
  if (!inputValue) {
    return true;
  }

  const normalizedInputValue = inputValue.toLowerCase();

  if (
    option.label &&
    fuzzysort.single(normalizedInputValue, option.label.toLowerCase())
  ) {
    return true;
  }

  if (option.data?.searchNames?.length) {
    const hasMatch = option.data.searchNames.some(
      (name: string) =>
        name && fuzzysort.single(normalizedInputValue, name.toLowerCase()),
    );
    if (hasMatch) return true;
  }

  if (option.data?.type === "hotel" && option.data?.citySearchNames?.length) {
    const hasMatch = option.data.citySearchNames.some(
      (cityName: string) =>
        cityName &&
        fuzzysort.single(normalizedInputValue, cityName.toLowerCase()),
    );
    if (hasMatch) return true;
  }

  return false;
};

/**
 * Formats a number by adding commas as thousand separators.
 *
 * Examples:
 *   formatPrice(1000) => "1,000"
 *   formatPrice(1000000) => "1,000,000"
 *
 * @param num - The number to format.
 * @returns A string representation of the number with commas.
 */
export const formatPrice = (price: number) => {
  return price.toLocaleString("en-US");
};

// Ensure checkout date is after check-in
export const validateAndAdjustCheckoutDate = (
  checkIn: Date | null,
  checkout: Date | null,
): { checkIn: Date | null; checkout: Date | null } => {
  if (!checkIn || !checkout) {
    return { checkIn, checkout };
  }

  const checkinDate = new Date(checkIn);
  const checkoutDate = new Date(checkout);

  if (checkinDate >= checkoutDate) {
    const newCheckoutDate = new Date(checkinDate);
    newCheckoutDate.setDate(newCheckoutDate.getDate() + 1);
    return { checkIn: checkinDate, checkout: newCheckoutDate };
  }

  return { checkIn: checkinDate, checkout: checkoutDate };
};

// Format date to YYYY-MM-DD
export const formatDateForStorage = (
  date: Date | null | undefined,
  defaultValue: string,
): string => {
  if (!date) return defaultValue;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
type NullableDate = Date | string | null | undefined;

// Parse stored date string or Date object to Date
export const parseStoredDate = (
  dateValue: NullableDate,
  defaultValue: string,
): Date => {
  if (!dateValue) return new Date(defaultValue);
  if (typeof dateValue === "string" && dateValue.includes("T")) {
    const dateOnly = dateValue.split("T")[0];
    return new Date(dateOnly);
  }

  if (dateValue instanceof Date) {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const day = String(dateValue.getDate()).padStart(2, "0");
    const localDateString = `${year}-${month}-${day}`;
    return new Date(localDateString);
  }

  return new Date(dateValue || defaultValue);
};

// Format amount as currency by country code
export function formatPriceByCountryCode(
  amount: number,
  countryCode: string,
): string {
  const countryToCurrencyMap: Record<string, string> = {
    JP: "JPY",
    US: "USD",
    VN: "VND",
    KR: "KRW",
    TH: "THB",
    SG: "SGD",
    CN: "CNY",
  };

  const currency = countryToCurrencyMap[countryCode] || "JPY";

  return amount.toLocaleString(undefined, {
    style: "currency",
    currency,
  });
}

// Remove trailing slash from pathname
export const removeTrailingSlashPathName = (pathName: string = ""): string => {
  return pathName?.endsWith("/") ? pathName?.slice(0, -1) : pathName;
};

// Validate stored check-in/out dates and fallback to defaults if invalid
export const checkAndValidateStoredDates = (
  checkinDateValue: Date | string | null | undefined,
  checkinDefaultValue: string,
  checkoutDateValue: Date | string | null | undefined,
  checkoutDefaultValue: string,
): { checkIn: Date; checkOut: Date } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let checkinDate = parseStoredDate(checkinDateValue, checkinDefaultValue);
  let checkoutDate = parseStoredDate(checkoutDateValue, checkoutDefaultValue);

  if (
    checkinDate < today ||
    checkoutDate < today ||
    checkoutDate < checkinDate
  ) {
    checkinDate = new Date(checkinDefaultValue);
    checkoutDate = new Date(checkoutDefaultValue);
  }

  return { checkIn: checkinDate, checkOut: checkoutDate };
};

export const generateRandomKey = (prefix: string) =>
  `${prefix}-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;

export { transformHotelsForMap } from "./mapUtils";

/**
 * Generic utility function to map text section data with buttons array.
 * Used across onsen, facilities, and other pages that have the buttons structure.
 * Handles type-safe transformation of CMS button data to TextSectionWithButtonsProps format.
 *
 * @param rawData - Raw CMS data containing text and buttons array
 * @returns Mapped TextSectionWithButtonsProps or undefined if no data
 */
export const mapTextSectionWithButtons = (
  rawData: unknown,
): TextSectionWithButtonsProps | undefined => {
  if (!rawData) {
    return undefined;
  }

  const data = rawData as { text?: string | null; buttons?: unknown[] | null };

  return {
    text: data.text || "",
    buttons: (data.buttons || [])
      .filter((btn): btn is NonNullable<typeof btn> => btn !== null)
      .map((btn) => ({
        id: (btn as { id?: string }).id || "",
        label: (btn as { label?: string | null }).label || null,
        margin: (btn as { margin?: string | null }).margin || null,
        padding: (btn as { padding?: string | null }).padding || null,
        borderRadius:
          (btn as { borderRadius?: string | null }).borderRadius || null,
        fontColor: (btn as { fontColor?: string | null }).fontColor || null,
        bgColor: (btn as { bgColor?: string | null }).bgColor || null,
        link: (btn as { link?: LinkConfig }).link || null,
        fontSize: (btn as { fontSize?: string | null }).fontSize || null,
        width: (btn as { width?: string | null }).width || null,
        pdf: (btn as { pdf?: UploadFile }).pdf || null,
        image: (btn as { image?: UploadFile }).image || null,
        secondaryLabel:
          (btn as { secondaryLabel?: string | null }).secondaryLabel || null,
      })),
  };
};

export const renderWithLineBreaks = (text: string): React.ReactNode[] =>
  text
    .split(/<br\s*\/?>/i)
    .flatMap((part, i, arr) =>
      i < arr.length - 1
        ? [part, React.createElement("br", { key: i })]
        : [part],
    );

export const isRichTextNonEmpty = (value?: string | null) => {
  if (!value) return false;

  return (
    value
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim().length !== 0
  );
};

export const isRichTextNonEmptyWithImage = (value?: string | null) => {
  if (!value) return false;

  const hasImage = /<img[\s\S]*?>/i.test(value);
  const hasScript = /<script[\s\S]*?>[\s\S]*?<\/script>/i.test(value);

  const textContent = value
    .replace(/<img[\s\S]*?>/gi, "") // remove img first so it doesn’t affect text check
    .replace(/<[^>]*>/g, "") // remove other tags
    .replace(/&nbsp;/g, "")
    .trim();

  return hasScript || hasImage || textContent.length > 0;
};
